import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaV3Provider, getToken } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// App Check (reCAPTCHA v3) — verifies that requests originate from this
// registered web app. Initialize before getAuth/getFirestore so all
// downstream Firebase calls carry the App Check token.
//
// REACT_APP_RECAPTCHA_SITE_KEY is the SITE key (public, safe to bundle).
// The corresponding SECRET key lives in Firebase Console's App Check config.
// If unset (local dev), skip init so Firebase keeps working without enforcement.
//
// Cold-start race guard: on a fresh load the app fires Firestore reads almost
// immediately, and if App Check's first token exchange races an unready
// reCAPTCHA it returns a 403 — which the SDK treats as a hard error and
// throttles for 24h, so every call then fails with permission-denied until a
// hard refresh. reCAPTCHA is preloaded in public/index.html; we still wait for
// grecaptcha.ready (with a short poll fallback) before initialising, then warm
// the token so the first real request carries a valid one. See #100.
if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const initAppCheck = () => {
    try {
      const appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      getToken(appCheck, false).catch((e) => console.warn('App Check token warm-up:', e?.message || e));
    } catch (e) {
      // Non-fatal: log to console but don't break the app
      console.warn('App Check init failed:', e?.message || e);
    }
  };
  if (window.grecaptcha && window.grecaptcha.ready) {
    window.grecaptcha.ready(initAppCheck);
  } else {
    let tries = 0;
    const timer = setInterval(() => {
      if (window.grecaptcha && window.grecaptcha.ready) { clearInterval(timer); window.grecaptcha.ready(initAppCheck); }
      else if (++tries >= 50) { clearInterval(timer); initAppCheck(); } // ~5s fallback: init anyway
    }, 100);
  }
}

export const auth = getAuth(app);
export const db = getFirestore(app);

// Session-only auth: user signs out when browser (all tabs) closes.
// Fires-and-forgets — if it fails, Firebase falls back to default local persistence.
setPersistence(auth, browserSessionPersistence).catch(() => {});
